
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { ArrowRight, Shield, Lock, Bell, Building2, Users2, LineChart, CheckCircle2, Play } from "lucide-react";
import { toast } from "sonner";

export function Index() {
  console.log("Rendering Index component");

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow">
        <div className="bg-gradient-to-br from-primary-50 via-white to-primary-100">
          <Navbar />
          
          {/* Hero Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-12 sm:pt-16 lg:pt-24 pb-12 sm:pb-16 lg:pb-20">
            <div className="text-center space-y-6 sm:space-y-8 animate-fade-in">
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold bg-gradient-to-r from-primary-800 to-primary-600 bg-clip-text text-transparent leading-tight flex items-center justify-center gap-4">
                La solution de gestion complète pour votre entreprise
                <span className="bg-primary-100 text-primary-600 text-sm px-2 py-1 rounded-full font-medium">Beta</span>
              </h1>
              <p className="text-lg sm:text-xl text-black max-w-3xl mx-auto leading-relaxed animate-fade-in delay-100">
                Que vous soyez commerçant local ou investisseur de la diaspora, Sibystock s'adapte à vos besoins. Gérez efficacement votre entreprise sur place ou à distance, gardez le contrôle total sur vos activités et collaborez en toute transparence avec vos équipes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center pt-4 animate-fade-in delay-200">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 bg-primary-600 hover:bg-primary-700 transition-all duration-300 transform hover:scale-105">
                    Commencer gratuitement <ArrowRight className="ml-2" />
                  </Button>
                </Link>
                <Link to="/login-owner" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 border-primary-300 hover:border-primary-400 hover:bg-primary-50 transition-all duration-300">
                    Se connecter
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Video Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pb-12 sm:pb-16 lg:pb-20">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white">
              <div className="relative pb-[56.25%]">
                <iframe 
                  src="https://www.youtube.com/embed/kEI_cd-gAIE" 
                  title="Présentation de Sibystock"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
            </div>
          </section>

          {/* Comment ça marche Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-12 sm:py-16 lg:py-20 bg-white rounded-3xl shadow-xl mx-4 sm:mx-8 lg:mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-primary-900 mb-8 sm:mb-16 animate-fade-in">
              Comment ça fonctionne ?
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
              <FeatureCard
                icon={Building2}
                title="1. Créez votre compte entreprise"
                description="Inscrivez-vous et sélectionnez votre type d'entreprise. Sibystock s'adapte automatiquement à vos besoins spécifiques avec une interface sur mesure."
              />
              <FeatureCard
                icon={Users2}
                title="2. Connectez-vous avec votre gérant"
                description="Un seul compte partagé entre vous et votre gérant en Afrique. Gardez le contrôle total en définissant précisément les fonctionnalités auxquelles il peut accéder."
              />
              <FeatureCard
                icon={Bell}
                title="3. Pilotez depuis l'étranger"
                description="Recevez des notifications instantanées pour chaque action importante. Ventes, stocks, transactions : gérez tout à distance, où que vous soyez dans le monde."
              />
              <FeatureCard
                icon={Shield}
                title="Protection maximale"
                description="Système de sécurité avancé rendant la fraude quasi impossible. Chaque action est tracée, horodatée et vérifiable en temps réel depuis l'étranger."
              />
              <FeatureCard
                icon={LineChart}
                title="Tableau de bord intelligent"
                description="Visualisez les performances de votre entreprise en temps réel. Prenez des décisions éclairées basées sur des données fiables, même à distance."
              />
              <FeatureCard
                icon={Lock}
                title="Contrôle total"
                description="Gérez les permissions d'accès depuis l'étranger. Activez ou désactivez les fonctionnalités selon vos besoins, en toute simplicité."
              />
            </div>
          </section>

          {/* Avantages Section */}
          <section className="bg-gradient-to-br from-primary-900 to-primary-800 text-white py-16 sm:py-20 lg:py-24 mt-12 sm:mt-16 lg:mt-20">
            <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center animate-fade-in">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8">
                La solution qui protège votre investissement à distance
              </h2>
              <p className="text-lg sm:text-xl mb-8 sm:mb-12 text-primary-100 max-w-3xl mx-auto">
                Avec Sibystock, gardez le contrôle total sur votre entreprise depuis l'étranger. Plus de fraudes, plus de pertes inexpliquées, plus de mauvaises surprises.
              </p>
              <Link to="/register">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-6 bg-white text-primary-900 hover:bg-primary-50 transition-all duration-300 transform hover:scale-105">
                  Créer un compte gratuitement
                </Button>
              </Link>
            </div>
          </section>

          {/* Fonctionnalités Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-20 lg:py-24">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="space-y-6 sm:space-y-8 animate-fade-in">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-900">
                  Pourquoi choisir Sibystock ?
                </h2>
                <div className="space-y-4 sm:space-y-6">
                  <BenefitItem
                    title="Gestion à distance simplifiée"
                    description="Un seul compte partagé entre vous et votre gérant, avec des accès différenciés pour une sécurité maximale, même à l'international."
                  />
                  <BenefitItem
                    title="Protection contre la fraude"
                    description="Système de traçabilité avancé rendant la fraude extrêmement difficile. Chaque action est enregistrée et vérifiable depuis l'étranger."
                  />
                  <BenefitItem
                    title="Adapté à votre activité"
                    description="Que vous soyez dans le commerce, l'élevage ou tout autre secteur, bénéficiez d'une interface parfaitement adaptée à vos besoins spécifiques."
                  />
                  <BenefitItem
                    title="En constante évolution"
                    description="De nouveaux types d'entreprises et fonctionnalités sont régulièrement ajoutés pour toujours mieux servir la diaspora africaine."
                  />
                </div>
                <Link to="/register" className="block">
                  <Button className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 mt-4 bg-primary-600 hover:bg-primary-700 transition-all duration-300 transform hover:scale-105">
                    Découvrir toutes les fonctionnalités <ArrowRight className="ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="lg:pl-12">
                <div className="bg-gradient-to-br from-primary-100 to-primary-50 p-6 sm:p-8 rounded-2xl shadow-xl animate-fade-in delay-100">
                  <h3 className="text-xl sm:text-2xl font-bold text-primary-900 mb-4 sm:mb-6">
                    Fonctionnalités principales
                  </h3>
                  <ul className="space-y-3 sm:space-y-4">
                    <FeatureListItem text="Gestion de stock en temps réel depuis l'étranger" />
                    <FeatureListItem text="Suivi des ventes et transactions à distance" />
                    <FeatureListItem text="Rapports financiers détaillés" />
                    <FeatureListItem text="Contrôle des accès avancé" />
                    <FeatureListItem text="Notifications en temps réel" />
                    <FeatureListItem text="Protection contre la fraude" />
                    <FeatureListItem text="Interface adaptée à votre activité" />
                    <FeatureListItem text="Support client réactif" />
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }: { icon: any, title: string, description: string }) {
  return (
    <div className="p-6 sm:p-8 rounded-xl bg-white shadow-lg border border-primary-100 hover:border-primary-300 transition-all duration-300 transform hover:scale-105 animate-fade-in">
      <div className="rounded-full bg-primary-100 p-3 sm:p-4 w-12 sm:w-14 h-12 sm:h-14 flex items-center justify-center mb-4 sm:mb-6">
        <Icon className="w-6 sm:w-7 h-6 sm:h-7 text-primary-600" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-primary-900 mb-2 sm:mb-3">{title}</h3>
      <p className="text-sm sm:text-base text-black leading-relaxed">{description}</p>
    </div>
  );
}

function BenefitItem({ title, description }: { title: string, description: string }) {
  return (
    <div className="space-y-1 sm:space-y-2 hover:translate-x-2 transition-transform duration-300">
      <h3 className="text-lg sm:text-xl font-semibold text-primary-900">{title}</h3>
      <p className="text-sm sm:text-base text-black">{description}</p>
    </div>
  );
}

function FeatureListItem({ text }: { text: string }) {
  return (
    <li className="flex items-center text-sm sm:text-base text-black hover:text-primary-600 transition-colors">
      <CheckCircle2 className="h-4 sm:h-5 w-4 sm:w-5 text-primary-500 mr-2 sm:mr-3 flex-shrink-0" />
      {text}
    </li>
  );
}

export default Index;
